/**
 * Client NDJSON streaming
 *
 * Envoie une requête HTTP (POST par défaut, JSON ou FormData) et consomme
 * la réponse ligne-par-ligne (application/x-ndjson). Chaque ligne doit être
 * un JSON valide — livrée au consommateur via `onEvent(evt)`.
 *
 * Pattern utilisé par les reprises longues (FPM, FSR V2, rachats, recalcul)
 * pour afficher une barre de progression en direct.
 *
 * Exemple :
 *   await streamNdjson({
 *     url: `${API_URL}/payments/bulk-fpm-generation?stream=1`,
 *     body: { year: 2026, month: 4 },
 *     onEvent: (evt) => { ... setProgress(...) ... }
 *   });
 */
export async function streamNdjson({
  url,
  method = 'POST',
  body,         // objet JSON -> auto-sérialisé (sinon on ignore)
  formData,     // FormData -> upload fichier + Authorization
  headers = {},
  onEvent
}) {
  const token = localStorage.getItem('accessToken');
  const finalHeaders = {
    Accept: 'application/x-ndjson',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers
  };

  let payload;
  if (formData) {
    payload = formData; // fetch gère Content-Type + boundary automatiquement
  } else if (body !== undefined && body !== null) {
    finalHeaders['Content-Type'] = finalHeaders['Content-Type'] || 'application/json';
    payload = JSON.stringify(body);
  }

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    body: payload
  });

  if (!response.ok || !response.body) {
    let errMsg = `Requête échouée (HTTP ${response.status})`;
    try {
      const j = await response.json();
      errMsg = j?.message || errMsg;
    } catch (_) { /* body pas JSON */ }
    const err = new Error(errMsg);
    err.status = response.status;
    throw err;
  }

  const reader  = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let finalResult  = null;
  let finalMessage = null;
  let succeeded    = true;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 1);
      if (!line) continue;
      let evt;
      try { evt = JSON.parse(line); } catch (_) { continue; }

      if (evt.type === 'end') {
        succeeded    = !!evt.success;
        finalResult  = evt.data || null;
        finalMessage = evt.message || null;
      }

      if (typeof onEvent === 'function') {
        try { onEvent(evt); } catch (_) { /* no-op */ }
      }
    }
  }

  if (!succeeded) {
    const err = new Error(finalMessage || 'Traitement terminé avec erreur');
    err.result = finalResult;
    throw err;
  }

  return finalResult;
}

export default streamNdjson;
